import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import axios from 'axios';
import toast from 'react-hot-toast';
import { motion, AnimatePresence } from 'motion/react';
import {
  BarChart3,
  TrendingUp,
  Coins,
  Users,
  Mail,
  Calendar,
  ChevronDown,
  Download,
  Search,
  Video,
  Mic,
  FileText,
  Sparkles,
  RefreshCw,
  ArrowUpDown,
  ExternalLink,
  HelpCircle,
  X,
} from 'lucide-react';
import {
  AreaChart,
  Area,
  BarChart,
  Bar,
  PieChart,
  Pie,
  Cell,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
} from 'recharts';
import { cn } from '../../lib/utils';
import AnimatedModal from '../../components/ui/AnimatedModal';
import RecordingPlaybackButton from '../screenings/RecordingPlaybackButton';
import useCandidateScoreModals from '../../hooks/useCandidateScoreModals';

// Helper: Format INR currency
const formatINR = (val) => {
  return '₹' + Number(val || 0).toLocaleString('en-IN', {
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  });
};

const formatINRDec = (val) => {
  return '₹' + Number(val || 0).toLocaleString('en-IN', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
};

// Helper: Get friendly feature name for OpenAI spend
const getFriendlyFeatureName = (feature) => {
  if (!feature) return 'Unknown Feature';
  
  const map = {
    // Resume / Scoring
    resume_scoring: 'Resume Scoring & Grading',
    resume_scoring_v2: 'Resume Scoring & Grading (v2)',
    resume_parsing: 'Resume Parsing',
    resume_parsing_v2: 'Resume Parsing (v2)',
    resume_text_extraction_fallback: 'Resume Parser Fallback',
    short_answer_grading: 'Short Answer Evaluation',
    passage_reading_scoring: 'Pronunciation Evaluation',
    
    // Proctoring
    video_proctoring: 'AI Video Proctoring',
    audio_proctoring: 'AI Audio Proctoring',
    
    // Interview
    interview_opening: 'Interview Opening Turn',
    interview_follow_up: 'Interview Agent Follow-up',
    ai_interview_follow_up: 'Interview Agent Follow-up (Diarized)',
    interview_plan: 'Interview Structure Planner',
    ai_interview_file_questions: 'Question Generation from Files',
    ai_interview_recommendations: 'Interview Fit Recommendations',
    ai_interview_repair: 'Interview Conversation Recovery',
    ai_interview_rubric: 'Interview Rubric Scoring',
    ai_interview_communication: 'Communication Skills Assessment',
    ai_interview_summary: 'Candidate Performance Summary',
    ai_transcript_segmentation: 'Diarization Segmentation',
    
    // Search / Job
    structured_search_query: 'Structured Search Parsing',
    ai_search: 'AI Semantic Search',
    job_parsing: 'Job Description Parsing',
    job_generation: 'Job Description Generation',
    job_recommendations: 'Job Recommendations',
    
    // Criteria
    criteria_generation: 'Criteria Rubric Generation',
    criteria_suggestion: 'Criteria Suggestion Engine',
    guided_expected_points: 'Expected Answer Generation',
    generate_yes_no_expected: 'Yes/No Expected Point Generation',
    generate_expected_fallback: 'Answer Generation Fallback',
    
    // Assessment
    assessment_recommendations: 'Assessment Recommendations',
    assessment_auto_answer_mc: 'MCQ Auto-Evaluation',
    assessment_auto_generate_mc_options: 'MCQ Option Generation',
    assessment_auto_answer_tf: 'True/False Auto-Evaluation',
    assessment_auto_answer_essay: 'Essay Question Scoring',
    assessment_auto_answer_open: 'Open-ended Scoring',
    analysis_summary: 'Integrity Analysis Summary',
    
    // Misc
    ocr: 'Image Text Extraction (OCR)',
    ocr_selective: 'Selective Image OCR',
    ping: 'Connection Health Ping'
  };
  
  if (map[feature]) return map[feature];
  
  // Fallback: humanize raw name
  return feature
    .replace(/[_-]/g, ' ')
    .replace(/\b\w/g, (c) => c.toUpperCase());
};

// Helper: Get date string YYYY-MM-DD

const getYYYYMMDD = (date) => {
  return date.toISOString().slice(0, 10);
};

// Date Presets Helper
const getDateRangeForPreset = (preset) => {
  const today = new Date();
  const from = new Date();
  
  switch (preset) {
    case 'today':
      return { from: getYYYYMMDD(today), to: getYYYYMMDD(today) };
    case 'pastWeek':
      from.setDate(today.getDate() - 7);
      return { from: getYYYYMMDD(from), to: getYYYYMMDD(today) };
    case 'pastMonth':
      from.setMonth(today.getMonth() - 1);
      return { from: getYYYYMMDD(from), to: getYYYYMMDD(today) };
    case 'past90Days':
      from.setDate(today.getDate() - 90);
      return { from: getYYYYMMDD(from), to: getYYYYMMDD(today) };
    default:
      // Default to past month
      from.setMonth(today.getMonth() - 1);
      return { from: getYYYYMMDD(from), to: getYYYYMMDD(today) };
  }
};

const COLORS = [
  '#4F46E5', // Indigo
  '#7C3AED', // Violet
  '#10B981', // Emerald
  '#F59E0B', // Amber
  '#EC4899', // Pink
  '#EF4444', // Red
  '#3B82F6', // Blue
  '#06B6D4', // Cyan
  '#8B5CF6', // Purple
  '#14B8A6', // Teal
];

export default function InternalUsageTracking() {
  const [preset, setPreset] = useState('pastMonth');
  const [dateRange, setDateRange] = useState(() => getDateRangeForPreset('pastMonth'));
  const [selectedOrgs, setSelectedOrgs] = useState([]);
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const dropdownRef = useRef(null);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsDropdownOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const [granularity, setGranularity] = useState('daily');
  
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [error, setError] = useState(null);

  const {
    openCandidateResumeBreakdown,
    handleInlineViewAssessmentScore,
    openInterviewSessionForCandidate,
    ScoreModals,
  } = useCandidateScoreModals();

  const handleViewActivityDetails = useCallback((log) => {
    if (!log.candidateId || !log.screeningId) {
      toast.error('Details unavailable - candidate lacks screening context.');
      return;
    }

    const candidate = {
      _id: log.candidateId,
      screeningId: log.screeningId,
      candidateEmail: log.candidateEmail || log.email,
      email: log.candidateEmail || log.email,
      interviewSessionId: log.type.includes('Video') || log.type.includes('Audio') ? log._id : undefined,
      interviewId: log.interviewId,
      assessmentId: log.assessmentId,
    };

    if (log.type.includes('Video') || log.type.includes('Audio')) {
      openInterviewSessionForCandidate(candidate);
    } else if (log.type.includes('Assessment')) {
      handleInlineViewAssessmentScore(candidate);
    } else if (log.type.includes('Resume')) {
      openCandidateResumeBreakdown(candidate);
    }
  }, [openCandidateResumeBreakdown, handleInlineViewAssessmentScore, openInterviewSessionForCandidate]);

  // Sorting & Searching for per-client table
  const [searchTerm, setSearchTerm] = useState('');
  const [sortField, setSortField] = useState('totalCostInr');
  const [sortAsc, setSortAsc] = useState(false);

  // Detail view state for single client
  const [selectedClientDetail, setSelectedClientDetail] = useState(null);
  const [activeSessionListModal, setActiveSessionListModal] = useState(null);

  // Activity usage logs state
  const [activitySearchTerm, setActivitySearchTerm] = useState('');
  const [activityPage, setActivityPage] = useState(1);

  // Toggle metric for Feature Breakdown Chart ('callCount' | 'costInr')
  const [featureChartMetric, setFeatureChartMetric] = useState('callCount');

  // Collapsible detailed interview cost breakdown state
  const [showInterviewDetails, setShowInterviewDetails] = useState(false);
  const [voiceAccordionOpen, setVoiceAccordionOpen] = useState(false);
  const [videoAccordionOpen, setVideoAccordionOpen] = useState(true);

  // Collapsible detailed credits breakdown state
  const [showCreditDetails, setShowCreditDetails] = useState(false);

  // Collapsible detailed OpenAI spend breakdown state
  const [showOpenAIDetails, setShowOpenAIDetails] = useState(false);

  // Table display mode: cost details vs credits details
  const [tableMode, setTableMode] = useState('cost'); // 'cost' | 'credits'

  const handleTableModeChange = (mode) => {
    setTableMode(mode);
    if (mode === 'credits') {
      setSortField('creditsUsed');
    } else {
      setSortField('totalCostInr');
    }
    setSortAsc(false);
  };

  // Fetch usage data
  const fetchData = useCallback(async (isRefresh = false) => {
    if (isRefresh) setRefreshing(true);
    else setLoading(true);
    setError(null);

    try {
      const params = {
        from: dateRange.from,
        to: dateRange.to,
        granularity,
      };
      if (selectedOrgs.length > 0) {
        params.orgId = selectedOrgs.join(',');
      }

      const response = await axios.get('/api/internal/usage-tracking', { params });
      setData(response.data);
      
      // If we currently have a detail modal open, update it
      if (selectedClientDetail) {
        const updatedDetail = response.data?.perOrg?.find(
          (o) => o.orgId === selectedClientDetail.orgId
        );
        if (updatedDetail) {
          setSelectedClientDetail(updatedDetail);
        }
      }
    } catch (err) {
      console.error(err);
      setError(err.response?.data?.message || 'Failed to fetch usage tracking statistics.');
      toast.error('Error fetching usage data');
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, [dateRange, selectedOrgs.join(','), granularity, selectedClientDetail]);

  // Trigger fetch on filter changes
  useEffect(() => {
    fetchData();
  }, [dateRange.from, dateRange.to, selectedOrgs.join(','), granularity]);

  // Handle Preset Changes
  const handlePresetChange = (newPreset) => {
    setPreset(newPreset);
    if (newPreset !== 'custom') {
      setDateRange(getDateRangeForPreset(newPreset));
    }
  };

  // Handle Custom Date Input Change
  const handleCustomDateChange = (field, val) => {
    setDateRange((prev) => ({ ...prev, [field]: val }));
  };

  // Sort helper
  const handleSort = (field) => {
    if (sortField === field) {
      setSortAsc(!sortAsc);
    } else {
      setSortField(field);
      setSortAsc(false);
    }
  };

  // Filter and Sort Client Data
  const filteredAndSortedClients = useMemo(() => {
    if (!data?.perOrg) return [];
    
    let result = [...data.perOrg];

    // Search filter
    if (searchTerm.trim()) {
      const term = searchTerm.toLowerCase();
      result = result.filter(
        (c) =>
          c.orgName.toLowerCase().includes(term) ||
          c.plan.toLowerCase().includes(term) ||
          c.status.toLowerCase().includes(term)
      );
    }

    // Sort
    result.sort((a, b) => {
      let aVal = a[sortField];
      let bVal = b[sortField];

      if (typeof aVal === 'string') {
        aVal = aVal.toLowerCase();
        bVal = bVal.toLowerCase();
      }

      if (aVal < bVal) return sortAsc ? -1 : 1;
      if (aVal > bVal) return sortAsc ? 1 : -1;
      return 0;
    });

    return result;
  }, [data?.perOrg, searchTerm, sortField, sortAsc]);

  // Export CSV Helper
  const exportClientsCSV = () => {
    if (!filteredAndSortedClients.length) return;

    const headers = [
      'Client Name',
      'Plan',
      'Status',
      'Total Cost (INR)',
      'OpenAI Cost (INR)',
      'AI Interview Cost (INR)',
      'LLM Calls',
      'Interview Sessions',
      'Voice Sessions',
      'Video Sessions',
      'Avg Duration (min)',
      'Video Credits (1.0 cr)',
      'Audio Credits (1.0 cr)',
      'Assessment Credits (0.5 cr)',
      'Total Credits Used',
      'PAYG Overage (INR)',
      'Emails Sent',
    ];

    const rows = filteredAndSortedClients.map((c) => [
      `"${c.orgName.replace(/"/g, '""')}"`,
      c.plan,
      c.status,
      c.totalCostInr,
      c.openaiCostInr,
      c.interviewCostInr,
      c.llmCalls,
      c.interviewSessions,
      c.voiceSessions,
      c.videoSessions,
      c.interviewDurationMinutes,
      c.videoInterviews * 1.0,
      c.audioInterviews * 1.0,
      c.assessments * 0.5,
      c.creditsUsed,
      c.creditsSpendInr,
      c.emailsSent,
    ]);

    const csvContent =
      'data:text/csv;charset=utf-8,' +
      [headers.join(','), ...rows.map((e) => e.join(','))].join('\n');

    const encodedUri = encodeURI(csvContent);
    const link = document.createElement('a');
    link.setAttribute('href', encodedUri);
    link.setAttribute(
      'download',
      `talentcrux_clients_usage_${dateRange.from}_to_${dateRange.to}.csv`
    );
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    toast.success('CSV Exported successfully!');
  };

  // Top Orgs Chart Data (top 7 by cost)
  const topOrgsChartData = useMemo(() => {
    if (!data?.perOrg) return [];
    return [...data.perOrg]
      .filter((o) => o.totalCostInr > 0)
      .slice(0, 7)
      .map((o) => ({
        name: o.orgName.length > 15 ? o.orgName.slice(0, 15) + '...' : o.orgName,
        openaiCostInr: Math.round(o.openaiCostInr),
        interviewCostInr: Math.round(o.interviewCostInr),
        totalCost: Math.round(o.totalCostInr),
      }));
  }, [data?.perOrg]);

  // Top Clients by Credits Used (top 5)
  const topClientsByCredits = useMemo(() => {
    if (!data?.perOrg) return [];
    return [...data.perOrg]
      .filter((o) => o.creditsUsed > 0)
      .sort((a, b) => b.creditsUsed - a.creditsUsed)
      .slice(0, 5);
  }, [data?.perOrg]);

  // Filter and paginate unified activity logs
  const filteredActivityLogs = useMemo(() => {
    if (!data?.usageLogs) return [];
    const searchLower = activitySearchTerm.toLowerCase();
    return data.usageLogs.filter((log) => {
      return (
        (log.candidateName || '').toLowerCase().includes(searchLower) ||
        (log.candidateEmail || '').toLowerCase().includes(searchLower) ||
        (log.orgName || '').toLowerCase().includes(searchLower) ||
        (log.type || '').toLowerCase().includes(searchLower) ||
        (log.screeningLocation || '').toLowerCase().includes(searchLower)
      );
    });
  }, [data?.usageLogs, activitySearchTerm]);

  const itemsPerPage = 10;
  const totalActivityPages = Math.ceil(filteredActivityLogs.length / itemsPerPage) || 1;
  const paginatedActivityLogs = useMemo(() => {
    const start = (activityPage - 1) * itemsPerPage;
    return filteredActivityLogs.slice(start, start + itemsPerPage);
  }, [filteredActivityLogs, activityPage]);

  // Reset page on search change
  useEffect(() => {
    setActivityPage(1);
  }, [activitySearchTerm]);

  return (
    <div className="flex-1 min-w-0 bg-slate-50/50">
      {/* Header section with Glassmorphism */}
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4 mb-6 border-b border-slate-200/80 pb-6">
        <div>
          <div className="flex items-center gap-2">
            <h1 className="text-2xl font-bold text-slate-900 tracking-tight">
              Usage & Cost Analytics
            </h1>
            <span className="bg-indigo-50 border border-indigo-100 text-indigo-700 px-2 py-0.5 text-xs font-semibold rounded-full flex items-center gap-1">
              <Sparkles className="w-3.5 h-3.5" />
              INR Metrics
            </span>
          </div>
          <p className="mt-1.5 text-slate-500 text-sm">
            Unified billing tracking, credits consumption, OpenAI APIs, and LiveKit AI Interview costs.
          </p>
        </div>
        
        <div className="flex items-center gap-2 self-end md:self-auto">
          <button
            type="button"
            onClick={() => fetchData(true)}
            disabled={loading || refreshing}
            className="inline-flex items-center gap-1.5 px-3.5 py-2 rounded-xl border border-slate-200 bg-white hover:bg-slate-50 text-slate-600 font-medium text-sm transition-all shadow-sm active:scale-95 disabled:opacity-50"
          >
            <RefreshCw className={cn('w-4 h-4', refreshing && 'animate-spin')} />
            Refresh
          </button>
        </div>
      </div>

      {/* Dynamic Filter Controls Row */}
      <div className="bg-white/70 backdrop-blur-md rounded-2xl border border-slate-200/80 p-5 shadow-sm mb-6 grid grid-cols-1 md:grid-cols-4 lg:grid-cols-5 gap-4">
        {/* Preset dropdown */}
        <div className="flex flex-col">
          <label className="text-xs font-semibold text-slate-500 uppercase tracking-wider mb-2 flex items-center gap-1">
            <Calendar className="w-3.5 h-3.5" /> Date Presets
          </label>
          <div className="relative">
            <select
              value={preset}
              onChange={(e) => handlePresetChange(e.target.value)}
              className="w-full rounded-xl border border-slate-200 bg-white/80 pl-3.5 pr-8 py-2.5 text-sm text-slate-700 hover:border-slate-300 focus:border-indigo-500 focus:ring-1 focus:ring-indigo-500 outline-none appearance-none transition-all cursor-pointer font-medium"
            >
              <option value="today">Today</option>
              <option value="pastWeek">Last 7 Days</option>
              <option value="pastMonth">Last 30 Days</option>
              <option value="past90Days">Last 90 Days</option>
              <option value="custom">Custom Date Range</option>
            </select>
            <ChevronDown className="w-4 h-4 text-slate-400 absolute right-3.5 top-1/2 -translate-y-1/2 pointer-events-none" />
          </div>
        </div>

        {/* Date: From */}
        <div className="flex flex-col">
          <label className="text-xs font-semibold text-slate-500 uppercase tracking-wider mb-2">
            From Date
          </label>
          <input
            type="date"
            disabled={preset !== 'custom'}
            value={dateRange.from}
            onChange={(e) => handleCustomDateChange('from', e.target.value)}
            className="w-full rounded-xl border border-slate-200 bg-white/80 px-3.5 py-2.5 text-sm text-slate-700 focus:border-indigo-500 focus:ring-1 focus:ring-indigo-500 outline-none disabled:bg-slate-100/50 disabled:text-slate-400 disabled:cursor-not-allowed transition-all font-medium"
          />
        </div>

        {/* Date: To */}
        <div className="flex flex-col">
          <label className="text-xs font-semibold text-slate-500 uppercase tracking-wider mb-2">
            To Date
          </label>
          <input
            type="date"
            disabled={preset !== 'custom'}
            value={dateRange.to}
            onChange={(e) => handleCustomDateChange('to', e.target.value)}
            className="w-full rounded-xl border border-slate-200 bg-white/80 px-3.5 py-2.5 text-sm text-slate-700 focus:border-indigo-500 focus:ring-1 focus:ring-indigo-500 outline-none disabled:bg-slate-100/50 disabled:text-slate-400 disabled:cursor-not-allowed transition-all font-medium"
          />
        </div>

        {/* Client Multi-select dropdown */}
        <div className="flex flex-col relative" ref={dropdownRef}>
          <label className="text-xs font-semibold text-slate-500 uppercase tracking-wider mb-2 flex items-center gap-1">
            <Users className="w-3.5 h-3.5" /> Client Filter
          </label>
          <button
            type="button"
            onClick={() => setIsDropdownOpen(!isDropdownOpen)}
            className="w-full flex items-center justify-between rounded-xl border border-slate-200 bg-white/80 px-3.5 py-2.5 text-sm text-slate-700 hover:border-slate-350 focus:border-indigo-500 focus:ring-1 focus:ring-indigo-500 outline-none transition-all cursor-pointer font-medium min-h-[42px]"
          >
            <span className="truncate pr-2 select-none">
              {selectedOrgs.length === 0
                ? 'All Clients'
                : selectedOrgs.length === 1
                  ? data?.orgList?.find(o => o.id === selectedOrgs[0])?.name || 'Selected Client'
                  : selectedOrgs.length <= 2
                    ? selectedOrgs.map(id => data?.orgList?.find(o => o.id === id)?.name || '').filter(Boolean).join(', ')
                    : `${selectedOrgs.length} Clients Selected`}
            </span>
            <ChevronDown className="w-4 h-4 text-slate-400" />
          </button>
          
          <AnimatePresence>
            {isDropdownOpen && (
              <motion.div
                initial={{ opacity: 0, y: 5 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: 5 }}
                className="absolute z-50 top-[70px] left-0 right-0 bg-white border border-slate-200 rounded-xl shadow-xl max-h-[300px] overflow-y-auto p-2 space-y-1"
              >
                <div className="flex justify-between items-center px-2 py-1 border-b border-slate-100 mb-1">
                  <button
                    type="button"
                    onClick={() => setSelectedOrgs([])}
                    className="text-[11px] text-slate-500 hover:text-indigo-600 font-semibold"
                  >
                    Clear All
                  </button>
                  <button
                    type="button"
                    onClick={() => setSelectedOrgs(data?.orgList?.map(o => o.id) || [])}
                    className="text-[11px] text-slate-500 hover:text-indigo-600 font-semibold"
                  >
                    Select All
                  </button>
                </div>
                
                <div className="space-y-0.5">
                  {data?.orgList?.map((o) => {
                    const isChecked = selectedOrgs.includes(o.id);
                    return (
                      <label
                        key={o.id}
                        className={cn(
                          "flex items-center gap-2.5 px-2.5 py-2 rounded-lg cursor-pointer text-sm font-medium hover:bg-slate-50 transition-all select-none",
                          isChecked && "bg-indigo-50/50 text-indigo-900"
                        )}
                      >
                        <input
                          type="checkbox"
                          checked={isChecked}
                          onChange={() => {
                            if (isChecked) {
                              setSelectedOrgs(selectedOrgs.filter(id => id !== o.id));
                            } else {
                              setSelectedOrgs([...selectedOrgs, o.id]);
                            }
                          }}
                          className="rounded text-indigo-600 focus:ring-indigo-500 border-slate-300 w-4 h-4 cursor-pointer"
                        />
                        <span className="truncate">{o.name}</span>
                      </label>
                    );
                  })}
                </div>
              </motion.div>
            )}
          </AnimatePresence>
        </div>

        {/* Granularity (daily/weekly/monthly) */}
        <div className="flex flex-col">
          <label className="text-xs font-semibold text-slate-500 uppercase tracking-wider mb-2">
            Granularity
          </label>
          <div className="bg-slate-100 p-1 rounded-xl flex gap-1 items-center h-[42px]">
            {['daily', 'weekly', 'monthly'].map((g) => (
              <button
                key={g}
                type="button"
                onClick={() => setGranularity(g)}
                className={cn(
                  'flex-1 text-center text-xs font-semibold py-1.5 rounded-lg capitalize transition-all',
                  granularity === g
                    ? 'bg-white text-indigo-700 shadow-sm'
                    : 'text-slate-500 hover:text-slate-800'
                )}
              >
                {g}
              </button>
            ))}
          </div>
        </div>
      </div>

      {/* Loading Skeleton */}
      {loading ? (
        <div className="space-y-6">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
            {[...Array(4)].map((_, i) => (
              <div key={i} className="h-32 bg-white rounded-2xl border border-slate-200 animate-pulse p-5">
                <div className="flex justify-between items-center mb-4">
                  <div className="h-4 w-24 bg-slate-200 rounded" />
                  <div className="h-8 w-8 bg-slate-200 rounded-full" />
                </div>
                <div className="h-6 w-32 bg-slate-200 rounded mb-2" />
                <div className="h-3.5 w-16 bg-slate-100 rounded" />
              </div>
            ))}
          </div>
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            <div className="lg:col-span-2 h-96 bg-white rounded-2xl border border-slate-200 animate-pulse p-6" />
            <div className="h-96 bg-white rounded-2xl border border-slate-200 animate-pulse p-6" />
          </div>
        </div>
      ) : error ? (
        <div className="rounded-2xl bg-red-50 border border-red-200 px-6 py-8 text-center text-red-700 mb-6 shadow-sm">
          <p className="font-semibold text-base mb-2">Could not retrieve usage metrics</p>
          <p className="text-sm opacity-90">{error}</p>
        </div>
      ) : (
        <motion.div
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.3 }}
          className="space-y-6"
        >
          {/* KPI CARDS ROW */}
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
            {/* CARD 1: Total cost */}
            <div className="relative overflow-hidden bg-gradient-to-br from-indigo-600 to-indigo-700 rounded-2xl text-white p-5 shadow-md hover:shadow-lg transition-all border border-indigo-500/10 group">
              <div className="absolute right-0 bottom-0 translate-x-3 translate-y-3 opacity-10 pointer-events-none group-hover:scale-110 transition-transform">
                <TrendingUp className="w-36 h-36" />
              </div>
              <div className="flex justify-between items-start mb-3">
                <span className="text-indigo-100 font-semibold text-xs uppercase tracking-wider">
                  Total Platform Spend
                </span>
                <span className="p-2 rounded-xl bg-indigo-500/30 text-indigo-100 border border-indigo-400/20">
                  <TrendingUp className="w-4 h-4" />
                </span>
              </div>
              <p className="text-3xl font-extrabold tracking-tight tabular-nums">
                {formatINR(data?.summary?.totalPlatformCostInr)}
              </p>
              <div className="mt-3 flex items-center gap-1 text-xs text-indigo-200">
                <span>≈ ${(data?.summary?.totalPlatformCostUsd || 0).toLocaleString()} USD</span>
                <span className="text-[10px] bg-indigo-500/40 px-1.5 py-0.5 rounded">1 USD = ₹{data?.summary?.usdToInr || 85}</span>
              </div>
            </div>

            {/* CARD 2: OpenAI API Spend */}
            <div className="bg-white rounded-2xl border border-slate-200/80 p-5 shadow-sm hover:shadow transition-all group flex flex-col justify-between">
              <div>
                <div className="flex justify-between items-start mb-2">
                  <span className="text-slate-500 font-semibold text-xs uppercase tracking-wider">
                    OpenAI API Spend
                  </span>
                  <button
                    type="button"
                    onClick={() => setShowOpenAIDetails(!showOpenAIDetails)}
                    className={cn(
                      "p-2 rounded-xl border transition-all text-xs font-semibold flex items-center gap-1 active:scale-95",
                      showOpenAIDetails 
                        ? "bg-violet-600 border-violet-600 text-white" 
                        : "bg-violet-50 border-violet-100 text-violet-600 hover:bg-violet-100"
                    )}
                    title="Toggle OpenAI Cost Breakdown"
                  >
                    <Sparkles className="w-4 h-4" />
                    <span>Breakdown</span>
                  </button>
                </div>
                <p className="text-2xl font-extrabold text-slate-900 tracking-tight tabular-nums">
                  {formatINR(data?.summary?.openaiCostInr)}
                </p>
              </div>
              <div className="mt-4 pt-3 border-t border-slate-100 flex items-center justify-between text-xs text-slate-500">
                <span>{data?.summary?.openaiCalls?.toLocaleString()} LLM Calls</span>
                <span className="font-medium text-violet-600">
                  {Math.round((data?.summary?.openaiCostInr / (data?.summary?.totalPlatformCostInr || 1)) * 100)}% of total
                </span>
              </div>
            </div>

            {/* CARD 3: AI Interview Cost */}
            <div className="bg-white rounded-2xl border border-slate-200/80 p-5 shadow-sm hover:shadow transition-all group flex flex-col justify-between">
              <div>
                <div className="flex justify-between items-start mb-2">
                  <span className="text-slate-500 font-semibold text-xs uppercase tracking-wider">
                    AI Interview Spend
                  </span>
                  <button
                    type="button"
                    onClick={() => setShowInterviewDetails(!showInterviewDetails)}
                    className={cn(
                      "p-2 rounded-xl border transition-all text-xs font-semibold flex items-center gap-1 active:scale-95",
                      showInterviewDetails 
                        ? "bg-emerald-600 border-emerald-600 text-white" 
                        : "bg-emerald-50 border-emerald-100 text-emerald-600 hover:bg-emerald-100"
                    )}
                    title="Toggle Detailed Cost Breakdown"
                  >
                    <Video className="w-4 h-4" />
                    <span>Breakdown</span>
                  </button>
                </div>
                <p className="text-2xl font-extrabold text-slate-900 tracking-tight tabular-nums">
                  {formatINR(data?.summary?.interviewCostInr)}
                </p>
              </div>
              <div className="mt-4 pt-3 border-t border-slate-100 flex items-center justify-between text-xs text-slate-500">
                <span>
                  {data?.summary?.interviewSessions?.toLocaleString()} Sessions · {data?.summary?.interviewDurationMinutes?.toLocaleString()} min
                </span>
                <span className="font-medium text-emerald-600">
                  {Math.round((data?.summary?.interviewCostInr / (data?.summary?.totalPlatformCostInr || 1)) * 100)}% of total
                </span>
              </div>
            </div>

            {/* CARD 4: AI Credits Consumed */}
            <div className="bg-white rounded-2xl border border-slate-200/80 p-5 shadow-sm hover:shadow transition-all group flex flex-col justify-between">
              <div>
                <div className="flex justify-between items-start mb-2">
                  <span className="text-slate-500 font-semibold text-xs uppercase tracking-wider">
                    AI Credits Consumed
                  </span>
                  <button
                    type="button"
                    onClick={() => setShowCreditDetails(!showCreditDetails)}
                    className={cn(
                      "p-2 rounded-xl border transition-all text-xs font-semibold flex items-center gap-1 active:scale-95",
                      showCreditDetails 
                        ? "bg-amber-600 border-amber-600 text-white" 
                        : "bg-amber-50 border-amber-100 text-amber-600 hover:bg-amber-100"
                    )}
                    title="Toggle Credits Consumption Audit"
                  >
                    <Coins className="w-4 h-4" />
                    <span>Audit</span>
                  </button>
                </div>
                <p className="text-2xl font-extrabold text-slate-900 tracking-tight tabular-nums">
                  {Number(data?.summary?.totalCreditsUsed || 0).toLocaleString()}
                </p>
              </div>
              <div className="mt-4 pt-3 border-t border-slate-100 flex items-center justify-between text-xs text-slate-500">
                <span className="truncate">Overage billed: {formatINR(data?.summary?.totalCreditsSpendInr)}</span>
                <span className="font-semibold text-amber-700 bg-amber-50 border border-amber-100 px-2 py-0.5 rounded-full text-[10px]">
                  PAYG Active
                </span>
              </div>
            </div>
          </div>

          {/* Detailed AI Interview Cost Breakdown Panel */}
          <AnimatePresence>
            {showInterviewDetails && data?.summary?.interviewDetails && (
              <motion.div
                initial={{ opacity: 0, height: 0 }}
                animate={{ opacity: 1, height: 'auto' }}
                exit={{ opacity: 0, height: 0 }}
                transition={{ duration: 0.3 }}
                className="overflow-hidden"
              >
                <div className="bg-white rounded-2xl border border-slate-200 p-6 shadow-sm mb-6 space-y-5">
                  <div className="flex items-center justify-between border-b border-slate-100 pb-3">
                    <h3 className="text-base font-bold text-slate-900 flex items-center gap-2">
                      <Video className="w-5 h-5 text-emerald-600" />
                      AI Interviews Cost Audit
                    </h3>
                    <span className="text-[10px] bg-slate-100 text-slate-500 font-semibold px-2 py-0.5 rounded-full">
                      Real-time aggregates
                    </span>
                  </div>

                  {/* Summary grid */}
                  <div className="grid grid-cols-2 sm:grid-cols-5 gap-4 text-center">
                    <div className="bg-slate-50/50 p-3 rounded-xl border border-slate-100">
                      <span className="text-[10px] font-bold text-slate-400 uppercase tracking-wider block">Sessions</span>
                      <strong className="text-base font-bold text-slate-800 tabular-nums">
                        {data.summary.interviewDetails.total.sessions}
                      </strong>
                    </div>
                    <div className="bg-slate-50/50 p-3 rounded-xl border border-slate-100">
                      <span className="text-[10px] font-bold text-slate-400 uppercase tracking-wider block">Total Minutes</span>
                      <strong className="text-base font-bold text-slate-800 tabular-nums">
                        {data.summary.interviewDetails.total.minutes} min
                      </strong>
                    </div>
                    <div className="bg-slate-50/50 p-3 rounded-xl border border-slate-100">
                      <span className="text-[10px] font-bold text-slate-400 uppercase tracking-wider block">Total Hours</span>
                      <strong className="text-base font-bold text-slate-800 tabular-nums">
                        {data.summary.interviewDetails.total.hours} hrs
                      </strong>
                    </div>
                    <div className="bg-slate-50/50 p-3 rounded-xl border border-slate-100">
                      <span className="text-[10px] font-bold text-slate-400 uppercase tracking-wider block">Avg per session</span>
                      <strong className="text-base font-bold text-slate-800 tabular-nums">
                        {data.summary.interviewDetails.total.avgDurationMinutes} min
                      </strong>
                    </div>
                    <div className="bg-slate-50/50 p-3 rounded-xl border border-slate-100">
                      <span className="text-[10px] font-bold text-slate-400 uppercase tracking-wider block">Median duration</span>
                      <strong className="text-base font-bold text-slate-800 tabular-nums">
                        {data.summary.interviewDetails.total.medianDurationMinutes} min
                      </strong>
                    </div>
                  </div>

                  {/* Accordion Panels */}
                  <div className="space-y-3">
                    
                    {/* Voice Accordion */}
                    <div className="border border-slate-150 rounded-xl overflow-hidden shadow-sm bg-white">
                      <button
                        type="button"
                        onClick={() => setVoiceAccordionOpen(!voiceAccordionOpen)}
                        className="w-full flex items-center justify-between px-4 py-3 bg-slate-50/50 hover:bg-slate-100/50 transition font-semibold text-xs text-slate-700 select-none border-b border-slate-100"
                      >
                        <span className="font-bold">
                          VOICE ({data.summary.interviewDetails.voice.sessions} SESSIONS, {data.summary.interviewDetails.voice.minutes} MIN)
                        </span>
                        <div className="flex items-center gap-2">
                          <span className="font-bold text-slate-900">{formatINRDec(data.summary.interviewDetails.voice.subtotalInr)}</span>
                          <ChevronDown className={cn("w-4 h-4 text-slate-400 transition-transform", voiceAccordionOpen && "rotate-180")} />
                        </div>
                      </button>

                      <AnimatePresence>
                        {voiceAccordionOpen && (
                          <motion.div
                            initial={{ height: 0, opacity: 0 }}
                            animate={{ height: 'auto', opacity: 1 }}
                            exit={{ height: 0, opacity: 0 }}
                            className="overflow-hidden"
                          >
                            <div className="p-4 space-y-2.5 text-xs text-slate-600 bg-white divide-y divide-slate-50">
                              <div className="flex justify-between items-center py-1">
                                <span>LiveKit (₹)</span>
                                <span className="font-semibold tabular-nums">{formatINRDec(data.summary.interviewDetails.voice.livekitInr)}</span>
                              </div>
                              <div className="flex justify-between items-center py-1">
                                <span>STT (₹)</span>
                                <span className="font-semibold tabular-nums">{formatINRDec(data.summary.interviewDetails.voice.sttInr)}</span>
                              </div>
                              <div className="flex justify-between items-center py-1">
                                <span>TTS (₹)</span>
                                <span className="font-semibold tabular-nums">{formatINRDec(data.summary.interviewDetails.voice.ttsInr)}</span>
                              </div>
                              <div className="flex justify-between items-center py-1">
                                <span>LLM Inference (₹)</span>
                                <span className="font-semibold tabular-nums">{formatINRDec(data.summary.interviewDetails.voice.llmInferenceInr)}</span>
                              </div>
                              <div className="flex justify-between items-center py-1">
                                <span>Observability (₹)</span>
                                <span className="font-semibold tabular-nums">{formatINRDec(data.summary.interviewDetails.voice.observabilityInr)}</span>
                              </div>
                              <div className="flex justify-between items-center py-1.5 font-bold text-slate-900 text-xs pt-2">
                                <span>Voice subtotal (₹)</span>
                                <span>{formatINRDec(data.summary.interviewDetails.voice.subtotalInr)}</span>
                              </div>
                              {data.summary.interviewDetails.voice.sessions > 0 && (
                                <div className="pt-2 text-[10px] text-slate-400 flex justify-between">
                                  <span>Avg cost per interview (Voice): {formatINRDec(data.summary.interviewDetails.voice.avgCostPerSessionInr)}</span>
                                  <span>Avg cost per min (Voice): {formatINRDec(data.summary.interviewDetails.voice.avgCostPerMinuteInr)}/min</span>
                                </div>
                              )}
                            </div>
                          </motion.div>
                        )}
                      </AnimatePresence>
                    </div>

                    {/* Video Accordion */}
                    <div className="border border-slate-150 rounded-xl overflow-hidden shadow-sm bg-white">
                      <button
                        type="button"
                        onClick={() => setVideoAccordionOpen(!videoAccordionOpen)}
                        className="w-full flex items-center justify-between px-4 py-3 bg-slate-50/50 hover:bg-slate-100/50 transition font-semibold text-xs text-slate-700 select-none border-b border-slate-100"
                      >
                        <span className="font-bold">
                          VIDEO ({data.summary.interviewDetails.video.sessions} SESSIONS, {data.summary.interviewDetails.video.minutes} MIN)
                        </span>
                        <div className="flex items-center gap-2">
                          <span className="font-bold text-slate-900">{formatINRDec(data.summary.interviewDetails.video.subtotalInr)}</span>
                          <ChevronDown className={cn("w-4 h-4 text-slate-400 transition-transform", videoAccordionOpen && "rotate-180")} />
                        </div>
                      </button>

                      <AnimatePresence>
                        {videoAccordionOpen && (
                          <motion.div
                            initial={{ height: 0, opacity: 0 }}
                            animate={{ height: 'auto', opacity: 1 }}
                            exit={{ height: 0, opacity: 0 }}
                            className="overflow-hidden"
                          >
                            <div className="p-4 space-y-2.5 text-xs text-slate-600 bg-white divide-y divide-slate-50">
                              <div className="flex justify-between items-center py-1">
                                <span>LiveKit (₹)</span>
                                <span className="font-semibold tabular-nums">{formatINRDec(data.summary.interviewDetails.video.livekitInr)}</span>
                              </div>
                              <div className="flex justify-between items-center py-1">
                                <span>STT (₹)</span>
                                <span className="font-semibold tabular-nums">{formatINRDec(data.summary.interviewDetails.video.sttInr)}</span>
                              </div>
                              <div className="flex justify-between items-center py-1">
                                <span>TTS (₹)</span>
                                <span className="font-semibold tabular-nums">{formatINRDec(data.summary.interviewDetails.video.ttsInr)}</span>
                              </div>
                              <div className="flex justify-between items-center py-1">
                                <span>LLM Inference (₹)</span>
                                <span className="font-semibold tabular-nums">{formatINRDec(data.summary.interviewDetails.video.llmInferenceInr)}</span>
                              </div>
                              <div className="flex justify-between items-center py-1">
                                <span>Observability (₹)</span>
                                <span className="font-semibold tabular-nums">{formatINRDec(data.summary.interviewDetails.video.observabilityInr)}</span>
                              </div>
                              <div className="flex justify-between items-center py-1.5 font-bold text-slate-900 text-xs pt-2">
                                <span>Video subtotal (₹)</span>
                                <span>{formatINRDec(data.summary.interviewDetails.video.subtotalInr)}</span>
                              </div>
                              {data.summary.interviewDetails.video.sessions > 0 && (
                                <div className="pt-2 text-[10px] text-slate-400 flex justify-between">
                                  <span>Avg cost per interview (Video): {formatINRDec(data.summary.interviewDetails.video.avgCostPerSessionInr)}</span>
                                  <span>Avg cost per min (Video): {formatINRDec(data.summary.interviewDetails.video.avgCostPerMinuteInr)}/min</span>
                                </div>
                              )}
                            </div>
                          </motion.div>
                        )}
                      </AnimatePresence>
                    </div>

                  </div>

                  {/* Summary Totals Row */}
                  <div className="border-t border-slate-100 pt-4 flex flex-col gap-2.5 text-xs font-semibold text-slate-700">
                    <div className="flex justify-between items-center">
                      <span className="font-bold">Total AI Interview Cost (₹)</span>
                      <strong className="text-sm font-black text-slate-900">{formatINRDec(data.summary.interviewDetails.total.subtotalInr)}</strong>
                    </div>
                    <div className="flex justify-between items-center">
                      <span>Avg cost per minute</span>
                      <strong className="text-slate-800">{formatINRDec(data.summary.interviewDetails.total.avgCostPerMinuteInr)}/min</strong>
                    </div>
                    <div className="flex justify-between items-center">
                      <span>Avg cost per interview</span>
                      <strong className="text-slate-800">{formatINRDec(data.summary.interviewDetails.total.avgCostPerSessionInr)}</strong>
                    </div>
                  </div>

                </div>
              </motion.div>
            )}
          </AnimatePresence>

          {/* Detailed OpenAI API Spend Breakdown Panel */}
          <AnimatePresence>
            {showOpenAIDetails && data?.featureBreakdown && (
              <motion.div
                initial={{ opacity: 0, height: 0 }}
                animate={{ opacity: 1, height: 'auto' }}
                exit={{ opacity: 0, height: 0 }}
                transition={{ duration: 0.3 }}
                className="overflow-hidden"
              >
                <div className="bg-white rounded-2xl border border-slate-200 p-6 shadow-sm mb-6 space-y-5">
                  <div className="flex items-center justify-between border-b border-slate-100 pb-3">
                    <h3 className="text-base font-bold text-slate-900 flex items-center gap-2">
                      <Sparkles className="w-5 h-5 text-violet-600" />
                      OpenAI API Cost Audit
                    </h3>
                    <span className="text-[10px] bg-slate-100 text-slate-500 font-semibold px-2 py-0.5 rounded-full">
                      Feature-wise breakdown
                    </span>
                  </div>

                  {/* Summary grid */}
                  <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 text-center">
                    <div className="bg-slate-50/50 p-3 rounded-xl border border-slate-100">
                      <span className="text-[10px] font-bold text-slate-400 uppercase tracking-wider block">LLM Calls</span>
                      <strong className="text-base font-bold text-slate-800 tabular-nums">
                        {data.summary.openaiCalls?.toLocaleString()}
                      </strong>
                    </div>
                    <div className="bg-slate-50/50 p-3 rounded-xl border border-slate-100">
                      <span className="text-[10px] font-bold text-slate-400 uppercase tracking-wider block">Input Tokens</span>
                      <strong className="text-base font-bold text-slate-800 tabular-nums">
                        {data.summary.openaiInputTokens?.toLocaleString()}
                      </strong>
                    </div>
                    <div className="bg-slate-50/50 p-3 rounded-xl border border-slate-100">
                      <span className="text-[10px] font-bold text-slate-400 uppercase tracking-wider block">Output Tokens</span>
                      <strong className="text-base font-bold text-slate-800 tabular-nums">
                        {data.summary.openaiOutputTokens?.toLocaleString()}
                      </strong>
                    </div>
                    <div className="bg-slate-50/50 p-3 rounded-xl border border-slate-100">
                      <span className="text-[10px] font-bold text-slate-400 uppercase tracking-wider block">Total OpenAI Cost</span>
                      <strong className="text-base font-bold text-violet-700 tabular-nums">
                        {formatINRDec(data.summary.openaiCostInr)}
                      </strong>
                    </div>
                  </div>

                  {/* Detailed Table */}
                  <div className="border border-slate-150 rounded-xl overflow-hidden shadow-sm bg-white">
                    <div className="overflow-x-auto">
                      <table className="min-w-full text-left text-xs whitespace-nowrap">
                        <thead className="bg-slate-50 text-slate-500 border-b border-slate-100">
                          <tr className="uppercase font-semibold tracking-wider text-[10px]">
                            <th className="py-2.5 pl-4 pr-3">Feature Area</th>
                            <th className="py-2.5 px-3 text-right">Calls</th>
                            <th className="py-2.5 px-3 text-right">Input Tokens</th>
                            <th className="py-2.5 px-3 text-right">Output Tokens</th>
                            <th className="py-2.5 px-3 text-right">Spend (INR)</th>
                            <th className="py-2.5 pl-3 pr-4 text-right">% of OpenAI Spend</th>
                          </tr>
                        </thead>
                        <tbody className="divide-y divide-slate-100 text-slate-700">
                          {data.featureBreakdown.map((fb, idx) => {
                            const totalOpenAISpend = data.summary.openaiCostInr || 1;
                            const pct = Math.min(100, Math.max(0, (fb.costInr / totalOpenAISpend) * 100));
                            return (
                              <tr key={fb.feature} className="hover:bg-slate-50/50 transition">
                                <td className="py-2.5 pl-4 pr-3 font-semibold text-slate-800">
                                  {getFriendlyFeatureName(fb.feature)}
                                  <span className="block text-[9px] text-slate-400 font-normal">{fb.feature}</span>
                                </td>
                                <td className="py-2.5 px-3 text-right tabular-nums font-medium text-slate-600">
                                  {fb.callCount?.toLocaleString()}
                                </td>
                                <td className="py-2.5 px-3 text-right tabular-nums text-slate-500">
                                  {fb.inputTokens?.toLocaleString()}
                                </td>
                                <td className="py-2.5 px-3 text-right tabular-nums text-slate-500">
                                  {fb.outputTokens?.toLocaleString()}
                                </td>
                                <td className="py-2.5 px-3 text-right font-bold text-slate-900 tabular-nums">
                                  {formatINRDec(fb.costInr)}
                                </td>
                                <td className="py-2.5 pl-3 pr-4 text-right">
                                  <div className="flex items-center justify-end gap-2">
                                    <span className="font-semibold text-violet-600 tabular-nums w-8">{pct.toFixed(0)}%</span>
                                    <div className="w-16 h-2 bg-slate-100 rounded-full overflow-hidden flex-shrink-0">
                                      <div
                                        style={{ width: `${pct}%` }}
                                        className="bg-violet-500 h-full"
                                      />
                                    </div>
                                  </div>
                                </td>
                              </tr>
                            );
                          })}
                          {data.featureBreakdown.length === 0 && (
                            <tr>
                              <td colSpan={6} className="py-8 text-center text-slate-400 font-medium">
                                No feature-wise logs found for selected period.
                              </td>
                            </tr>
                          )}
                        </tbody>
                      </table>
                    </div>
                  </div>
                </div>
              </motion.div>
            )}
          </AnimatePresence>

          {/* Detailed Credit Consumption Breakdown Panel */}
          <AnimatePresence>
            {showCreditDetails && data?.summary && (
              <motion.div
                initial={{ opacity: 0, height: 0 }}
                animate={{ opacity: 1, height: 'auto' }}
                exit={{ opacity: 0, height: 0 }}
                transition={{ duration: 0.3 }}
                className="overflow-hidden"
              >
                <div className="bg-white rounded-2xl border border-slate-200 p-6 shadow-sm mb-6 space-y-6">
                  <div className="flex items-center justify-between border-b border-slate-100 pb-3">
                    <h3 className="text-base font-bold text-slate-900 flex items-center gap-2">
                      <Coins className="w-5 h-5 text-amber-600" />
                      Credits Consumption & Allocation Audit
                    </h3>
                    <span className="text-[10px] bg-slate-100 text-slate-500 font-semibold px-2 py-0.5 rounded-full">
                      Real-time aggregates
                    </span>
                  </div>

                  <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                    {/* Left Column: Credit Consumption by Feature */}
                    <div className="space-y-4">
                      <div>
                        <h4 className="text-xs font-bold text-slate-400 uppercase tracking-wider mb-2.5">
                          Credits Consumed by Platform Feature
                        </h4>
                        <div className="divide-y divide-slate-100 text-xs text-slate-600 bg-slate-50/50 border border-slate-150 rounded-xl p-4 space-y-2">
                          <div className="flex justify-between items-center py-1">
                            <span className="font-medium">Video AI Interviews (1.0 credit each)</span>
                            <span className="font-bold text-slate-800 tabular-nums">
                              {(data.summary.videoInterviews || 0).toLocaleString()} sessions → {(data.summary.videoInterviews * 1.0).toFixed(1)} credits
                            </span>
                          </div>
                          <div className="flex justify-between items-center py-1">
                            <span className="font-medium">Audio AI Interviews (1.0 credit each)</span>
                            <span className="font-bold text-slate-800 tabular-nums">
                              {(data.summary.audioInterviews || 0).toLocaleString()} sessions → {(data.summary.audioInterviews * 1.0).toFixed(1)} credits
                            </span>
                          </div>
                          <div className="flex justify-between items-center py-1">
                            <span className="font-medium">Candidate Assessments (0.5 credits each)</span>
                            <span className="font-bold text-slate-800 tabular-nums">
                              {(data.summary.assessments || 0).toLocaleString()} applicants → {(data.summary.assessments * 0.5).toFixed(1)} credits
                            </span>
                          </div>
                          <div className="flex justify-between items-center py-2 border-t border-slate-200 font-bold text-slate-900 text-sm">
                             <span>Total Platform Credits Consumed</span>
                             <span className="text-indigo-700">{data.summary.totalCreditsUsed.toFixed(1)} Credits</span>
                           </div>
                           {/* Visual Credits Distribution Bar */}
                           {(() => {
                             const totalCr = data.summary.totalCreditsUsed || 1;
                             const videoPct = ((data.summary.videoInterviews * 1.0) / totalCr) * 100;
                             const audioPct = ((data.summary.audioInterviews * 1.0) / totalCr) * 100;
                             const assessmentPct = ((data.summary.assessments * 0.5) / totalCr) * 100;
                             return (
                               <div className="mt-3.5 space-y-1.5">
                                 <div className="h-3 w-full bg-slate-100 rounded-full overflow-hidden flex shadow-inner">
                                   {videoPct > 0 && (
                                     <div
                                       style={{ width: `${videoPct}%` }}
                                       className="bg-indigo-600 h-full transition-all duration-500"
                                       title={`Video AI Interviews: ${videoPct.toFixed(1)}%`}
                                     />
                                   )}
                                   {audioPct > 0 && (
                                     <div
                                       style={{ width: `${audioPct}%` }}
                                       className="bg-emerald-500 h-full transition-all duration-500"
                                       title={`Audio AI Interviews: ${audioPct.toFixed(1)}%`}
                                     />
                                   )}
                                   {assessmentPct > 0 && (
                                     <div
                                       style={{ width: `${assessmentPct}%` }}
                                       className="bg-amber-500 h-full transition-all duration-500"
                                       title={`Candidate Assessments: ${assessmentPct.toFixed(1)}%`}
                                     />
                                   )}
                                 </div>
                                 <div className="flex justify-between text-[10px] text-slate-400 px-0.5">
                                   <span className="flex items-center gap-1">
                                     <span className="w-2 h-2 rounded-full bg-indigo-600 block" /> Video ({videoPct.toFixed(0)}%)
                                   </span>
                                   <span className="flex items-center gap-1">
                                     <span className="w-2 h-2 rounded-full bg-emerald-500 block" /> Audio ({audioPct.toFixed(0)}%)
                                   </span>
                                   <span className="flex items-center gap-1">
                                     <span className="w-2 h-2 rounded-full bg-amber-500 block" /> Assessments ({assessmentPct.toFixed(0)}%)
                                   </span>
                                 </div>
                               </div>
                             );
                           })()}
                        </div>
                      </div>

                      {/* Info alert */}
                      <div className="p-3 bg-amber-50/50 border border-amber-100 text-[11px] text-amber-800 rounded-xl">
                        💡 **Note:** Resume uploads / scoring and platform OCR usage are charged under local PAYG rates directly in INR and do not consume plan credits.
                      </div>
                    </div>

                    {/* Right Column: Top Clients by Credit Consumption */}
                    <div>
                      <h4 className="text-xs font-bold text-slate-400 uppercase tracking-wider mb-2.5">
                        Top Clients by Credit Consumption
                      </h4>
                      <div className="space-y-3 max-h-[200px] overflow-y-auto pr-1">
                        {topClientsByCredits.map((client) => (
                          <div
                            key={client.orgId}
                            className="bg-white border border-slate-150 rounded-xl p-3.5 flex justify-between items-center hover:bg-slate-50 transition duration-150 shadow-sm"
                          >
                            <div className="space-y-1">
                              <div className="flex items-center gap-2">
                                <span className="font-bold text-xs text-slate-900 truncate max-w-[150px]">
                                  {client.orgName}
                                </span>
                                <span className="bg-slate-100 text-slate-500 text-[9px] px-1.5 py-0.5 rounded capitalize font-semibold">
                                  {client.plan}
                                </span>
                              </div>
                              <p className="text-[10px] text-slate-400">
                                {(client.videoInterviews * 1.0).toFixed(0)}cr video · {(client.audioInterviews * 1.0).toFixed(0)}cr audio · {(client.assessments * 0.5).toFixed(1)}cr assessments
                              </p>
                            </div>
                            <div className="text-right">
                              <span className="text-xs font-extrabold text-slate-900 tabular-nums block">
                                {client.creditsUsed.toFixed(1)} credits
                              </span>
                              {client.creditsSpendInr > 0 && (
                                <span className="text-[9px] font-semibold text-rose-600">
                                  +{formatINR(client.creditsSpendInr)} overage
                                </span>
                              )}
                            </div>
                          </div>
                        ))}
                        {topClientsByCredits.length === 0 && (
                          <p className="text-center text-xs text-slate-400 py-6">
                            No clients have consumed credits for the selected date range.
                          </p>
                        )}
                      </div>
                    </div>
                  </div>

                </div>
              </motion.div>
            )}
          </AnimatePresence>

          {/* SECOND ROW STATS CARDS */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <div 
              onClick={() => setActiveSessionListModal('video')}
              className="bg-white border border-slate-200/60 rounded-xl px-4 py-3 shadow-sm flex items-center gap-3 cursor-pointer hover:border-indigo-300 hover:shadow-md hover:scale-[1.01] transition-all"
            >
              <div className="p-2 rounded-lg bg-indigo-50 text-indigo-600">
                <Video className="w-4 h-4" />
              </div>
              <div>
                <p className="text-xs text-slate-500 font-medium">Video Sessions</p>
                <p className="text-base font-bold text-slate-800 tabular-nums">
                  {(data?.summary?.videoInterviews || 0).toLocaleString()}
                </p>
              </div>
            </div>
            <div 
              onClick={() => setActiveSessionListModal('audio')}
              className="bg-white border border-slate-200/60 rounded-xl px-4 py-3 shadow-sm flex items-center gap-3 cursor-pointer hover:border-emerald-300 hover:shadow-md hover:scale-[1.01] transition-all"
            >
              <div className="p-2 rounded-lg bg-emerald-50 text-emerald-600">
                <Mic className="w-4 h-4" />
              </div>
              <div>
                <p className="text-xs text-slate-500 font-medium">Audio Sessions</p>
                <p className="text-base font-bold text-slate-800 tabular-nums">
                  {(data?.summary?.audioInterviews || 0).toLocaleString()}
                </p>
              </div>
            </div>
            <div 
              onClick={() => setActiveSessionListModal('assessment')}
              className="bg-white border border-slate-200/60 rounded-xl px-4 py-3 shadow-sm flex items-center gap-3 cursor-pointer hover:border-cyan-300 hover:shadow-md hover:scale-[1.01] transition-all"
            >
              <div className="p-2 rounded-lg bg-cyan-50 text-cyan-600">
                <FileText className="w-4 h-4" />
              </div>
              <div>
                <p className="text-xs text-slate-500 font-medium">Assessments & Resume Scores</p>
                <p className="text-base font-bold text-slate-800 tabular-nums">
                  {((data?.summary?.assessments || 0) + (data?.summary?.resumeScores || 0)).toLocaleString()}
                </p>
              </div>
            </div>
            <div className="bg-white border border-slate-200/60 rounded-xl px-4 py-3 shadow-sm flex items-center gap-3">
              <div className="p-2 rounded-lg bg-pink-50 text-pink-600">
                <Mail className="w-4 h-4" />
              </div>
              <div>
                <p className="text-xs text-slate-500 font-medium">Emails Transmitted</p>
                <p className="text-base font-bold text-slate-800 tabular-nums">
                  {(data?.summary?.emailsSent || 0).toLocaleString()}
                </p>
              </div>
            </div>
          </div>

          {/* MAIN VISUALIZATIONS SECTION */}
          <div className="w-full">
            
            {/* Chart 1: Daily Cost Trend (Stacked Area Chart) */}
            <div className="bg-white border border-slate-200 rounded-2xl shadow-sm p-5 flex flex-col justify-between">
              <div className="mb-4">
                <h3 className="text-base font-bold text-slate-900">Cost Accrual Trend</h3>
                <p className="text-xs text-slate-500">
                  OpenAI API and LiveKit AI Interview daily costs in INR
                </p>
              </div>
              <div className="h-72 w-full">
                {!data || !data.timeSeries || data.timeSeries.length === 0 ? (
                  <div className="h-full flex items-center justify-center text-sm text-slate-400">
                    No time-series data found for selected period.
                  </div>
                ) : (
                  <ResponsiveContainer width="100%" height="100%">
                    <AreaChart data={data?.timeSeries} margin={{ top: 10, right: 10, left: -20, bottom: 0 }}>
                      <defs>
                        <linearGradient id="colorOpenAI" x1="0" y1="0" x2="0" y2="1">
                          <stop offset="5%" stopColor="#7C3AED" stopOpacity={0.2} />
                          <stop offset="95%" stopColor="#7C3AED" stopOpacity={0} />
                        </linearGradient>
                        <linearGradient id="colorInterview" x1="0" y1="0" x2="0" y2="1">
                          <stop offset="5%" stopColor="#10B981" stopOpacity={0.2} />
                          <stop offset="95%" stopColor="#10B981" stopOpacity={0} />
                        </linearGradient>
                      </defs>
                      <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" />
                      <XAxis dataKey="dateLabel" tick={{ fontSize: 10 }} stroke="#94a3b8" />
                      <YAxis tick={{ fontSize: 10 }} stroke="#94a3b8" tickFormatter={(v) => `₹${v}`} />
                      <Tooltip
                        formatter={(val, name) => [formatINRDec(val), name]}
                        labelStyle={{ fontWeight: 'bold', fontSize: '12px' }}
                        contentStyle={{ borderRadius: '12px', borderColor: '#e2e8f0' }}
                      />
                      <Legend verticalAlign="top" height={36} iconType="circle" iconSize={8} wrapperStyle={{ fontSize: '12px' }} />
                      <Area
                        type="monotone"
                        name="OpenAI Cost (INR)"
                        dataKey="llmCostInr"
                        stackId="1"
                        stroke="#7C3AED"
                        fillOpacity={1}
                        fill="url(#colorOpenAI)"
                      />
                      <Area
                        type="monotone"
                        name="Interview Cost (INR)"
                        dataKey="interviewCostInr"
                        stackId="1"
                        stroke="#10B981"
                        fillOpacity={1}
                        fill="url(#colorInterview)"
                      />
                    </AreaChart>
                  </ResponsiveContainer>
                )}
              </div>
            </div>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {/* Chart 3: Top clients by usage cost */}
            <div className="bg-white border border-slate-200 rounded-2xl shadow-sm p-5 flex flex-col justify-between">
              <div className="mb-4">
                <h3 className="text-base font-bold text-slate-900">Top Clients by Total Costs</h3>
                <p className="text-xs text-slate-500">Aggregate platform costs for high-usage clients</p>
              </div>
              <div className="h-72 w-full">
                {!data || !data.perOrg || topOrgsChartData.length === 0 ? (
                  <div className="h-full flex items-center justify-center text-sm text-slate-400">
                    No client costs recorded.
                  </div>
                ) : (
                  <ResponsiveContainer width="100%" height="100%">
                    <BarChart data={topOrgsChartData} margin={{ top: 10, right: 10, left: -20, bottom: 0 }}>
                      <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" />
                      <XAxis dataKey="name" tick={{ fontSize: 9 }} stroke="#94a3b8" />
                      <YAxis tick={{ fontSize: 10 }} stroke="#94a3b8" tickFormatter={(v) => `₹${v}`} />
                      <Tooltip formatter={(v) => formatINR(v)} />
                      <Legend verticalAlign="top" height={36} iconType="rect" wrapperStyle={{ fontSize: '11px' }} />
                      <Bar name="OpenAI Cost (INR)" dataKey="openaiCostInr" stackId="stack1" fill="#7C3AED" />
                      <Bar name="Interview Cost (INR)" dataKey="interviewCostInr" stackId="stack1" fill="#10B981" />
                    </BarChart>
                  </ResponsiveContainer>
                )}
              </div>
            </div>

            {/* Chart 4: Feature-wise OpenAI API Usage */}
            <div className="bg-white border border-slate-200 rounded-2xl shadow-sm p-5 flex flex-col justify-between">
              <div className="mb-4 flex items-center justify-between gap-4">
                <div>
                  <h3 className="text-base font-bold text-slate-900">LLM Usage by Platform Feature</h3>
                  <p className="text-xs text-slate-500">OpenAI token usage broken down by feature area</p>
                </div>
                <select
                  value={featureChartMetric}
                  onChange={(e) => setFeatureChartMetric(e.target.value)}
                  className="rounded-lg border border-slate-200 bg-white px-2 py-1 text-xs text-slate-700 outline-none"
                >
                  <option value="callCount">LLM Calls Count</option>
                  <option value="costInr">Cost (INR)</option>
                </select>
              </div>
              <div className="h-72 w-full">
                {!data || !data.featureBreakdown || data.featureBreakdown.length === 0 ? (
                  <div className="h-full flex items-center justify-center text-sm text-slate-400">
                    No feature usage logs recorded.
                  </div>
                ) : (
                  <ResponsiveContainer width="100%" height="100%">
                    <BarChart
                      data={data?.featureBreakdown}
                      layout="vertical"
                      margin={{ top: 10, right: 10, left: 10, bottom: 0 }}
                    >
                      <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" />
                      <XAxis type="number" tick={{ fontSize: 10 }} stroke="#94a3b8" />
                      <YAxis
                        type="category"
                        dataKey="feature"
                        tick={{ fontSize: 9 }}
                        stroke="#94a3b8"
                        width={100}
                      />
                      <Tooltip
                        formatter={(val) =>
                          featureChartMetric === 'costInr' ? formatINRDec(val) : val?.toLocaleString()
                        }
                      />
                      <Bar
                        name={featureChartMetric === 'costInr' ? 'Spent (INR)' : 'OpenAI Calls'}
                        dataKey={featureChartMetric}
                        fill="#4F46E5"
                        radius={[0, 4, 4, 0]}
                      />
                    </BarChart>
                  </ResponsiveContainer>
                )}
              </div>
            </div>
          </div>

          {/* TABLE: CLIENT USAGE BREAKDOWN */}
          <div className="bg-white border border-slate-200 rounded-2xl shadow-sm overflow-hidden">
            <div className="px-5 py-4 border-b border-slate-200/80 bg-slate-50/50 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
              <div>
                <h3 className="text-base font-bold text-slate-900">
                  {tableMode === 'cost' ? 'Per-Client Cost Breakdown' : 'Per-Client Credits Breakdown'}
                </h3>
                <p className="text-xs text-slate-500">
                  {tableMode === 'cost' 
                    ? 'Aggregated breakdown of usage logs for each client organization' 
                    : 'Credits consumed by feature for each client organization'}
                </p>
              </div>
              
              <div className="flex flex-wrap items-center gap-2">
                {/* Table Mode Switcher */}
                <div className="bg-slate-100 p-0.5 rounded-lg flex border border-slate-200/80 mr-2">
                  <button
                    type="button"
                    onClick={() => handleTableModeChange('cost')}
                    className={cn(
                      "px-3 py-1 rounded-md text-[10px] font-semibold transition-all",
                      tableMode === 'cost'
                        ? "bg-white text-slate-900 shadow-sm"
                        : "text-slate-500 hover:text-slate-800"
                    )}
                  >
                    Cost View
                  </button>
                  <button
                    type="button"
                    onClick={() => handleTableModeChange('credits')}
                    className={cn(
                      "px-3 py-1 rounded-md text-[10px] font-semibold transition-all",
                      tableMode === 'credits'
                        ? "bg-white text-slate-900 shadow-sm"
                        : "text-slate-500 hover:text-slate-800"
                    )}
                  >
                    Credits View
                  </button>
                </div>

                {/* Search */}
                <div className="relative">
                  <input
                    type="text"
                    placeholder="Search client, plan..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    className="pl-8 pr-3.5 py-1.5 w-48 rounded-xl border border-slate-200 bg-white text-xs outline-none focus:border-indigo-500 focus:ring-1 focus:ring-indigo-500"
                  />
                  <Search className="w-3.5 h-3.5 text-slate-400 absolute left-2.5 top-1/2 -translate-y-1/2" />
                </div>

                {/* CSV Download */}
                <button
                  type="button"
                  onClick={exportClientsCSV}
                  className="inline-flex items-center gap-1 bg-white hover:bg-slate-50 border border-slate-200 text-slate-700 px-3 py-1.5 rounded-xl text-xs font-semibold shadow-sm transition-all"
                >
                  <Download className="w-3.5 h-3.5" />
                  Export CSV
                </button>
              </div>
            </div>

            <div className="overflow-x-auto">
              <table className="min-w-full text-left text-sm whitespace-nowrap">
                <thead className="border-b border-slate-100 bg-slate-50/60 text-slate-500">
                  {tableMode === 'cost' ? (
                    <tr className="text-xs uppercase font-semibold select-none">
                      <th className="py-3.5 pl-5 pr-3 font-medium">Client Name</th>
                      <th className="py-3.5 px-3 font-medium">Plan</th>
                      <th className="py-3.5 px-3 font-medium">Status</th>
                      <th className="py-3.5 px-3 font-medium text-right cursor-pointer hover:text-slate-800" onClick={() => handleSort('totalCostInr')}>
                        <div className="flex items-center justify-end gap-1">
                          Total Spent (INR)
                          <ArrowUpDown className="w-3 h-3" />
                        </div>
                      </th>
                      <th className="py-3.5 px-3 font-medium text-right cursor-pointer hover:text-slate-800" onClick={() => handleSort('openaiCostInr')}>
                        <div className="flex items-center justify-end gap-1">
                          OpenAI (INR)
                          <ArrowUpDown className="w-3 h-3" />
                        </div>
                      </th>
                      <th className="py-3.5 px-3 font-medium text-right cursor-pointer hover:text-slate-800" onClick={() => handleSort('interviewCostInr')}>
                        <div className="flex items-center justify-end gap-1">
                          LiveKit (INR)
                          <ArrowUpDown className="w-3 h-3" />
                        </div>
                      </th>
                      <th className="py-3.5 px-3 font-medium text-right cursor-pointer hover:text-slate-800" onClick={() => handleSort('llmCalls')}>
                        <div className="flex items-center justify-end gap-1">
                          LLM Calls
                          <ArrowUpDown className="w-3 h-3" />
                        </div>
                      </th>
                      <th className="py-3.5 px-3 font-medium text-right cursor-pointer hover:text-slate-800" onClick={() => handleSort('interviewSessions')}>
                        <div className="flex items-center justify-end gap-1">
                          Sessions
                          <ArrowUpDown className="w-3 h-3" />
                        </div>
                      </th>
                      <th className="py-3.5 px-3 font-medium text-right cursor-pointer hover:text-slate-800" onClick={() => handleSort('creditsUsed')}>
                        <div className="flex items-center justify-end gap-1">
                          Credits Used
                          <ArrowUpDown className="w-3 h-3" />
                        </div>
                      </th>
                      <th className="py-3.5 px-3 font-medium text-right cursor-pointer hover:text-slate-800" onClick={() => handleSort('creditsSpendInr')}>
                        <div className="flex items-center justify-end gap-1">
                          PAYG (INR)
                          <ArrowUpDown className="w-3 h-3" />
                        </div>
                      </th>
                      <th className="py-3.5 pl-3 pr-5 font-medium text-center">Action</th>
                    </tr>
                  ) : (
                    <tr className="text-xs uppercase font-semibold select-none">
                      <th className="py-3.5 pl-5 pr-3 font-medium">Client Name</th>
                      <th className="py-3.5 px-3 font-medium">Plan</th>
                      <th className="py-3.5 px-3 font-medium">Status</th>
                      <th className="py-3.5 px-3 font-medium text-right cursor-pointer hover:text-slate-800" onClick={() => handleSort('videoInterviews')}>
                        <div className="flex items-center justify-end gap-1">
                          Video Credits (1.0 cr)
                          <ArrowUpDown className="w-3 h-3" />
                        </div>
                      </th>
                      <th className="py-3.5 px-3 font-medium text-right cursor-pointer hover:text-slate-800" onClick={() => handleSort('audioInterviews')}>
                        <div className="flex items-center justify-end gap-1">
                          Audio Credits (1.0 cr)
                          <ArrowUpDown className="w-3 h-3" />
                        </div>
                      </th>
                      <th className="py-3.5 px-3 font-medium text-right cursor-pointer hover:text-slate-800" onClick={() => handleSort('assessments')}>
                        <div className="flex items-center justify-end gap-1">
                          Assessments (0.5 cr)
                          <ArrowUpDown className="w-3 h-3" />
                        </div>
                      </th>
                      <th className="py-3.5 px-3 font-medium text-right cursor-pointer hover:text-slate-800" onClick={() => handleSort('creditsUsed')}>
                        <div className="flex items-center justify-end gap-1">
                          Total Credits
                          <ArrowUpDown className="w-3 h-3" />
                        </div>
                      </th>
                      <th className="py-3.5 px-3 font-medium text-right cursor-pointer hover:text-slate-800" onClick={() => handleSort('creditsSpendInr')}>
                        <div className="flex items-center justify-end gap-1">
                          PAYG Overage (INR)
                          <ArrowUpDown className="w-3 h-3" />
                        </div>
                      </th>
                      <th className="py-3.5 pl-3 pr-5 font-medium text-center">Action</th>
                    </tr>
                  )}
                </thead>
                <tbody className="divide-y divide-slate-100 text-slate-700">
                  {filteredAndSortedClients.map((c) => (
                    <tr
                      key={c.orgId}
                      className="hover:bg-slate-50/50 transition duration-150"
                    >
                      <td className="py-3.5 pl-5 pr-3 font-semibold text-slate-900 max-w-xs truncate">
                        {c.orgName}
                      </td>
                      <td className="py-3.5 px-3">
                        <span className="capitalize text-xs font-semibold px-2 py-0.5 rounded-full bg-slate-100 text-slate-600 border border-slate-200/50">
                          {c.plan}
                        </span>
                      </td>
                      <td className="py-3.5 px-3">
                        <span
                          className={cn(
                            'inline-flex items-center rounded-full px-2 py-0.5 text-[10px] font-bold border',
                            c.status === 'active'
                              ? 'bg-emerald-50 border-emerald-200/50 text-emerald-700'
                              : 'bg-slate-100 border-slate-200 text-slate-500'
                          )}
                        >
                          {c.status}
                        </span>
                      </td>
                      {tableMode === 'cost' ? (
                        <>
                          <td className="py-3.5 px-3 text-right font-bold text-slate-900 tabular-nums">
                            {formatINR(c.totalCostInr)}
                          </td>
                          <td className="py-3.5 px-3 text-right text-slate-80 tabular-nums">
                            {formatINR(c.openaiCostInr)}
                          </td>
                          <td className="py-3.5 px-3 text-right text-slate-80 tabular-nums">
                            {formatINR(c.interviewCostInr)}
                          </td>
                          <td className="py-3.5 px-3 text-right font-medium text-slate-500 tabular-nums">
                            {c.llmCalls?.toLocaleString()}
                          </td>
                          <td className="py-3.5 px-3 text-right font-medium text-slate-500 tabular-nums">
                            {c.interviewSessions?.toLocaleString()}
                          </td>
                          <td className="py-3.5 px-3 text-right font-bold text-slate-800 tabular-nums">
                            {c.creditsUsed?.toFixed(1)}
                          </td>
                          <td className="py-3.5 px-3 text-right text-slate-500 tabular-nums">
                            {formatINR(c.creditsSpendInr)}
                          </td>
                        </>
                      ) : (
                        <>
                          <td className="py-3.5 px-3 text-right text-slate-600 tabular-nums font-semibold">
                            {(c.videoInterviews * 1.0).toFixed(1)}
                          </td>
                          <td className="py-3.5 px-3 text-right text-slate-600 tabular-nums font-semibold">
                            {(c.audioInterviews * 1.0).toFixed(1)}
                          </td>
                          <td className="py-3.5 px-3 text-right text-slate-600 tabular-nums font-semibold">
                            {(c.assessments * 0.5).toFixed(1)}
                          </td>
                          <td className="py-3.5 px-3 text-right font-bold text-indigo-700 tabular-nums">
                            {c.creditsUsed?.toFixed(1)}
                          </td>
                          <td className="py-3.5 px-3 text-right text-slate-500 tabular-nums">
                            {formatINR(c.creditsSpendInr)}
                          </td>
                        </>
                      )}
                      <td className="py-3.5 pl-3 pr-5 text-center">
                        <button
                          type="button"
                          onClick={() => setSelectedClientDetail(c)}
                          className="inline-flex items-center gap-1 text-xs font-bold text-indigo-600 hover:text-indigo-800 transition"
                        >
                          Details
                          <ExternalLink className="w-3 h-3" />
                        </button>
                      </td>
                    </tr>
                  ))}
                  {filteredAndSortedClients.length === 0 && (
                    <tr>
                      <td colSpan={tableMode === 'cost' ? 11 : 9} className="py-10 text-center text-slate-400 text-sm">
                        No clients found matching the search criteria.
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>

          {/* TABLE: ACTIVITY USAGE LOGS */}
          <div className="bg-white border border-slate-200 rounded-2xl shadow-sm overflow-hidden mt-6">
            <div className="px-5 py-4 border-b border-slate-200/80 bg-slate-50/50 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
              <div>
                <h3 className="text-base font-bold text-slate-900">System Activity Usage Logs</h3>
                <p className="text-xs text-slate-500">
                  Comprehensive audit trail of interviews, test assessments, and resume scores across all clients
                </p>
              </div>

              {/* Search Activity Logs */}
              <div className="relative">
                <input
                  type="text"
                  placeholder="Search activity, client, candidate..."
                  value={activitySearchTerm}
                  onChange={(e) => setActivitySearchTerm(e.target.value)}
                  className="pl-8 pr-3.5 py-1.5 w-64 rounded-xl border border-slate-200 bg-white text-xs outline-none focus:border-indigo-500 focus:ring-1 focus:ring-indigo-500"
                />
                <Search className="absolute left-2.5 top-2.5 w-3.5 h-3.5 text-slate-400" />
              </div>
            </div>

            <div className="overflow-x-auto">
              <table className="w-full text-left border-collapse">
                <thead>
                  <tr className="border-b border-slate-250 bg-slate-50 text-xs font-semibold text-slate-500 select-none uppercase tracking-wider">
                    <th className="py-3.5 pl-5 pr-3 font-medium">Candidate</th>
                    <th className="py-3.5 px-3 font-medium">Client</th>
                    <th className="py-3.5 px-3 font-medium">Activity Type</th>
                    <th className="py-3.5 px-3 font-medium">Credits</th>
                    <th className="py-3.5 px-3 font-medium">Screening Location</th>
                    <th className="py-3.5 pl-3 pr-3 font-medium text-right">Date & Time</th>
                    <th className="py-3.5 pl-3 pr-5 font-medium text-center">Action</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100 text-xs text-slate-700">
                  {paginatedActivityLogs.map((log) => (
                    <tr key={log._id} className="hover:bg-slate-50/30 transition duration-150">
                      {/* Candidate Name & Email */}
                      <td className="py-3.5 pl-5 pr-3">
                        <div className="font-semibold text-slate-900">{log.candidateName}</div>
                        <div className="text-[10px] text-slate-400">{log.candidateEmail}</div>
                      </td>

                      {/* Client organization */}
                      <td className="py-3.5 px-3 font-semibold text-slate-700">
                        {log.orgName}
                      </td>

                      {/* Activity Type Badge */}
                      <td className="py-3.5 px-3">
                        <span className={cn(
                          "inline-flex items-center rounded-full px-2 py-0.5 text-[9px] font-bold border capitalize",
                          log.type.includes('Video') && "bg-indigo-50 border-indigo-100 text-indigo-700",
                          log.type.includes('Audio') && "bg-emerald-50 border-emerald-100 text-emerald-700",
                          log.type.includes('Assessment') && "bg-cyan-50 border-cyan-100 text-cyan-700",
                          log.type.includes('Resume') && "bg-purple-50 border-purple-100 text-purple-700"
                        )}>
                          {log.type}
                        </span>
                      </td>

                      {/* Credits Used */}
                      <td className="py-3.5 px-3 font-bold text-slate-900 tabular-nums">
                        {log.creditsUsed > 0 ? `${log.creditsUsed.toFixed(1)} cr` : '—'}
                      </td>

                      {/* Screening Location (Job > Screening) */}
                      <td className="py-3.5 px-3 text-slate-500 font-medium max-w-xs truncate" title={log.screeningLocation}>
                        {log.screeningLocation}
                      </td>

                      {/* Date & Time */}
                      <td className="py-3.5 pl-3 pr-3 text-right text-slate-500 tabular-nums">
                        {log.date ? new Date(log.date).toLocaleString('en-IN', {
                          day: '2-digit',
                          month: 'short',
                          year: 'numeric',
                          hour: '2-digit',
                          minute: '2-digit',
                        }) : '—'}
                      </td>

                      {/* Action Button */}
                      <td className="py-3.5 pl-3 pr-5 text-center">
                        <button
                          type="button"
                          disabled={!log.candidateId || !log.screeningId}
                          onClick={() => handleViewActivityDetails(log)}
                          className="inline-flex items-center gap-1 text-xs font-bold text-indigo-600 hover:text-indigo-800 transition disabled:opacity-30 disabled:cursor-not-allowed"
                          title={(!log.candidateId || !log.screeningId) ? 'Detailed logs not available for this record' : undefined}
                        >
                          {log.type.includes('Video') || log.type.includes('Audio') ? 'Watch Replay' : 'View Score'}
                          <ExternalLink className="w-3 h-3" />
                        </button>
                      </td>
                    </tr>
                  ))}
                  {paginatedActivityLogs.length === 0 && (
                    <tr>
                      <td colSpan={7} className="py-10 text-center text-slate-400 text-sm">
                        No activity usage records found matching filters.
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>

            {/* Pagination Controls */}
            {totalActivityPages > 1 && (
              <div className="px-5 py-3 border-t border-slate-100 bg-slate-50/50 flex items-center justify-between gap-3 text-xs">
                <span className="text-slate-500">
                  Showing page <strong className="text-slate-800 font-bold">{activityPage}</strong> of <strong className="text-slate-800 font-bold">{totalActivityPages}</strong> ({filteredActivityLogs.length} total logs)
                </span>
                <div className="flex gap-2">
                  <button
                    type="button"
                    disabled={activityPage === 1}
                    onClick={() => setActivityPage(prev => prev - 1)}
                    className="px-3 py-1 bg-white hover:bg-slate-50 border border-slate-200 rounded-lg font-semibold shadow-sm transition disabled:opacity-50 text-slate-700 font-medium"
                  >
                    Previous
                  </button>
                  <button
                    type="button"
                    disabled={activityPage === totalActivityPages}
                    onClick={() => setActivityPage(prev => prev + 1)}
                    className="px-3 py-1 bg-white hover:bg-slate-50 border border-slate-200 rounded-lg font-semibold shadow-sm transition disabled:opacity-50 text-slate-700 font-medium"
                  >
                    Next
                  </button>
                </div>
              </div>
            )}
          </div>
        </motion.div>
      )}

      {/* CLIENT DETAIL MODAL WITH SENSORY BREAKDOWN */}
      <AnimatePresence>
        {selectedClientDetail && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/60 backdrop-blur-sm p-4 overflow-y-auto"
            onClick={() => setSelectedClientDetail(null)}
          >
            <motion.div
              initial={{ scale: 0.95, opacity: 0, y: 15 }}
              animate={{ scale: 1, opacity: 1, y: 0 }}
              exit={{ scale: 0.95, opacity: 0, y: 15 }}
              transition={{ duration: 0.2 }}
              className="w-full max-w-2xl rounded-2xl bg-white border border-slate-200 shadow-2xl overflow-hidden my-8"
              onClick={(e) => e.stopPropagation()}
            >
              {/* Modal Header */}
              <div className="px-6 py-4 bg-slate-900 text-white flex justify-between items-center">
                <div>
                  <h3 className="text-lg font-bold">{selectedClientDetail.orgName}</h3>
                  <p className="text-xs text-slate-400">Detailed usage log dashboard breakdown</p>
                </div>
                <button
                  type="button"
                  onClick={() => setSelectedClientDetail(null)}
                  className="text-slate-400 hover:text-white p-1 rounded-lg hover:bg-white/10 transition"
                >
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>

              {/* Modal Content */}
              <div className="p-6 space-y-6 max-h-[80vh] overflow-y-auto">
                
                {/* Meta details */}
                <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 bg-slate-50 border border-slate-200/60 p-4 rounded-xl text-center">
                  <div>
                    <span className="text-[10px] uppercase font-bold text-slate-400 tracking-wider">Plan Type</span>
                    <p className="text-sm font-bold text-slate-800 capitalize mt-1">{selectedClientDetail.plan}</p>
                  </div>
                  <div>
                    <span className="text-[10px] uppercase font-bold text-slate-400 tracking-wider">Status</span>
                    <p className="text-sm font-bold text-slate-800 capitalize mt-1">{selectedClientDetail.status}</p>
                  </div>
                  <div>
                    <span className="text-[10px] uppercase font-bold text-slate-400 tracking-wider">Credits Consumed</span>
                    <p className="text-sm font-bold text-indigo-600 mt-1 tabular-nums">{selectedClientDetail.creditsUsed?.toFixed(1)}</p>
                  </div>
                  <div>
                    <span className="text-[10px] uppercase font-bold text-slate-400 tracking-wider">Total Accrued Cost</span>
                    <p className="text-sm font-extrabold text-slate-950 mt-1">{formatINRDec(selectedClientDetail.totalCostInr)}</p>
                  </div>
                </div>

                {/* Section: OpenAI usage */}
                <div className="space-y-3">
                  <h4 className="text-xs font-bold text-slate-400 uppercase tracking-wider flex items-center gap-1.5 border-b border-slate-100 pb-1.5">
                    <Coins className="w-3.5 h-3.5 text-violet-600" /> OpenAI API Call Metric
                  </h4>
                  <div className="grid grid-cols-2 gap-4">
                    <div className="bg-slate-50/50 rounded-xl p-3 border border-slate-100">
                      <span className="text-[10px] text-slate-500 block">Total API Invocations</span>
                      <span className="text-base font-bold text-slate-800 tabular-nums">
                        {selectedClientDetail.llmCalls?.toLocaleString()} calls
                      </span>
                    </div>
                    <div className="bg-slate-50/50 rounded-xl p-3 border border-slate-100">
                      <span className="text-[10px] text-slate-500 block">Est. Cost (INR)</span>
                      <span className="text-base font-bold text-slate-800 tabular-nums">
                        {formatINRDec(selectedClientDetail.openaiCostInr)}
                      </span>
                    </div>
                  </div>
                  <div className="text-[11px] text-slate-500 pl-1 flex items-center gap-4">
                    <span>Input Tokens: <strong className="text-slate-700 font-semibold">{selectedClientDetail.llmInputTokens?.toLocaleString()}</strong></span>
                    <span>Output Tokens: <strong className="text-slate-700 font-semibold">{selectedClientDetail.llmOutputTokens?.toLocaleString()}</strong></span>
                  </div>
                </div>

                {/* Section: LiveKit AI Interviews */}
                <div className="space-y-3">
                  <h4 className="text-xs font-bold text-slate-400 uppercase tracking-wider flex items-center gap-1.5 border-b border-slate-100 pb-1.5">
                    <Video className="w-3.5 h-3.5 text-emerald-600" /> LiveKit AI Interviews
                  </h4>
                  <div className="grid grid-cols-3 gap-4">
                    <div className="bg-slate-50/50 rounded-xl p-3 border border-slate-100">
                      <span className="text-[10px] text-slate-500 block">Total Sessions</span>
                      <span className="text-base font-bold text-slate-800 tabular-nums">
                        {selectedClientDetail.interviewSessions || 0}
                      </span>
                    </div>
                    <div className="bg-slate-50/50 rounded-xl p-3 border border-slate-100">
                      <span className="text-[10px] text-slate-500 block">Total Duration</span>
                      <span className="text-base font-bold text-slate-800 tabular-nums">
                        {Math.round(selectedClientDetail.interviewDurationMinutes || 0)} min
                      </span>
                    </div>
                    <div className="bg-slate-50/50 rounded-xl p-3 border border-slate-100">
                      <span className="text-[10px] text-slate-500 block">Est. Cost (INR)</span>
                      <span className="text-base font-bold text-slate-800 tabular-nums">
                        {formatINRDec(selectedClientDetail.interviewCostInr)}
                      </span>
                    </div>
                  </div>
                  <div className="text-[11px] text-slate-500 pl-1 flex items-center gap-4">
                    <span>Voice calls: <strong className="text-slate-700 font-semibold">{selectedClientDetail.voiceSessions} sessions</strong></span>
                    <span>Video calls: <strong className="text-slate-700 font-semibold">{selectedClientDetail.videoSessions} sessions</strong></span>
                  </div>
                </div>

                {/* Section: Plan Credits Breakdown */}
                <div className="space-y-3">
                  <h4 className="text-xs font-bold text-slate-400 uppercase tracking-wider flex items-center gap-1.5 border-b border-slate-100 pb-1.5">
                    <Coins className="w-3.5 h-3.5 text-indigo-600" /> Plan Credits Breakdown
                  </h4>
                  <div className="divide-y divide-slate-100 text-xs text-slate-600 bg-slate-50 border border-slate-200/60 rounded-xl p-4 space-y-2">
                    <div className="flex justify-between items-center py-1">
                      <span className="font-medium">Video AI Interviews (1.0 cr each)</span>
                      <span className="font-bold text-slate-800 tabular-nums">
                        {selectedClientDetail.videoInterviews || 0} sessions → {(selectedClientDetail.videoInterviews * 1.0).toFixed(1)} cr
                      </span>
                    </div>
                    <div className="flex justify-between items-center py-1">
                      <span className="font-medium">Audio AI Interviews (1.0 cr each)</span>
                      <span className="font-bold text-slate-800 tabular-nums">
                        {selectedClientDetail.audioInterviews || 0} sessions → {(selectedClientDetail.audioInterviews * 1.0).toFixed(1)} cr
                      </span>
                    </div>
                    <div className="flex justify-between items-center py-1">
                      <span className="font-medium">Candidate Assessments (0.5 cr each)</span>
                      <span className="font-bold text-slate-800 tabular-nums">
                        {selectedClientDetail.assessments || 0} applicants → {(selectedClientDetail.assessments * 0.5).toFixed(1)} cr
                      </span>
                    </div>
                    <div className="flex justify-between items-center py-2 border-t border-slate-200 font-bold text-slate-900 text-xs pt-2">
                      <span>Total Plan Credits Consumed</span>
                      <span className="text-indigo-600">{selectedClientDetail.creditsUsed?.toFixed(1)} Credits</span>
                    </div>
                  </div>
                </div>

                {/* Section: PAYG credit items */}
                <div className="space-y-3">
                  <h4 className="text-xs font-bold text-slate-400 uppercase tracking-wider flex items-center gap-1.5 border-b border-slate-100 pb-1.5">
                    <Sparkles className="w-3.5 h-3.5 text-amber-600" /> PAYG On-Demand Consumption
                  </h4>
                  <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                    <div className="bg-slate-50/30 p-2.5 rounded-lg border border-slate-100 text-center">
                      <span className="text-[10px] text-slate-400 block">Video interviews</span>
                      <strong className="text-sm text-slate-800 tabular-nums">{selectedClientDetail.videoInterviews}</strong>
                    </div>
                    <div className="bg-slate-50/30 p-2.5 rounded-lg border border-slate-100 text-center">
                      <span className="text-[10px] text-slate-400 block">Audio interviews</span>
                      <strong className="text-sm text-slate-800 tabular-nums">{selectedClientDetail.audioInterviews}</strong>
                    </div>
                    <div className="bg-slate-50/30 p-2.5 rounded-lg border border-slate-100 text-center">
                      <span className="text-[10px] text-slate-400 block">Assessments</span>
                      <strong className="text-sm text-slate-800 tabular-nums">{selectedClientDetail.assessments}</strong>
                    </div>
                    <div className="bg-slate-50/30 p-2.5 rounded-lg border border-slate-100 text-center">
                      <span className="text-[10px] text-slate-400 block">Resume Scores</span>
                      <strong className="text-sm text-slate-800 tabular-nums">{selectedClientDetail.resumeScores}</strong>
                    </div>
                  </div>
                  <div className="bg-slate-50 border border-slate-200/80 rounded-xl p-3 flex justify-between items-center text-xs">
                    <span className="text-slate-500 font-medium">Credits consumed on-demand:</span>
                    <strong className="text-slate-900 font-bold">{formatINRDec(selectedClientDetail.creditsSpendInr)}</strong>
                  </div>
                </div>

                {/* Section: Email logs */}
                <div className="flex justify-between items-center border-t border-slate-100 pt-4 text-xs">
                  <div className="flex items-center gap-1.5">
                    <Mail className="w-4 h-4 text-pink-500" />
                    <span className="text-slate-500 font-medium">SMTP outbound messages sent:</span>
                  </div>
                  <strong className="text-slate-900 font-extrabold text-sm tabular-nums">
                    {selectedClientDetail.emailsSent?.toLocaleString()} messages
                  </strong>
                </div>

              </div>

              {/* Modal Footer */}
              <div className="px-6 py-4 bg-slate-50 border-t border-slate-100 flex justify-end">
                <button
                  type="button"
                  onClick={() => setSelectedClientDetail(null)}
                  className="px-4 py-2 bg-slate-900 hover:bg-slate-800 text-white font-medium text-xs rounded-xl shadow-sm transition"
                >
                  Close panel
                </button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Drilldown details modal */}
      <AnimatedModal
        isOpen={activeSessionListModal !== null}
        onClose={() => setActiveSessionListModal(null)}
        ariaLabelledBy="session-list-modal-title"
        contentClassName="max-w-5xl rounded-2xl bg-white shadow-2xl overflow-hidden"
      >
        <div className="flex max-h-[85vh] flex-col">
          {/* Modal Header */}
          <div className="flex items-center justify-between border-b border-slate-100 px-6 py-4">
            <div>
              <h2 id="session-list-modal-title" className="text-lg font-bold text-slate-900">
                {activeSessionListModal === 'video' && 'Video Sessions List'}
                {activeSessionListModal === 'audio' && 'Audio Sessions List'}
                {activeSessionListModal === 'assessment' && 'Assessments & Resume Scores List'}
              </h2>
              <p className="text-xs text-slate-500">
                {activeSessionListModal === 'video' && 'Detailed breakdown of video interview sessions.'}
                {activeSessionListModal === 'audio' && 'Detailed breakdown of audio/voice interview sessions.'}
                {activeSessionListModal === 'assessment' && 'Detailed logs of online test assessments and scored resumes.'}
              </p>
            </div>
            <button
              onClick={() => setActiveSessionListModal(null)}
              className="rounded-lg p-1.5 text-slate-400 hover:bg-slate-100 hover:text-slate-600 transition"
            >
              <X className="h-5 w-5" />
            </button>
          </div>

          {/* Modal Body */}
          <div className="flex-1 overflow-y-auto px-6 py-4">
            <SessionListTable
              type={activeSessionListModal}
              data={
                activeSessionListModal === 'video'
                  ? data?.videoSessionsList
                  : activeSessionListModal === 'audio'
                  ? data?.audioSessionsList
                  : data?.assessmentsAndResumeScoresList
              }
            />
          </div>
        </div>
      </AnimatedModal>
      <ScoreModals />
    </div>
  );
}

function SessionListTable({ type, data }) {
  const [searchTerm, setSearchTerm] = useState('');

  const {
    openCandidateResumeBreakdown,
    handleInlineViewAssessmentScore,
    openInterviewSessionForCandidate,
    ScoreModals,
  } = useCandidateScoreModals();
  
  if (!data || data.length === 0) {
    return (
      <div className="text-center py-12 text-sm text-slate-400">
        No records found matching filters.
      </div>
    );
  }

  const handleViewActivityDetails = (row) => {
    if (!row.candidateId || !row.screeningId) {
      toast.error('Details unavailable - candidate lacks screening context.');
      return;
    }

    const candidate = {
      _id: row.candidateId,
      screeningId: row.screeningId,
      candidateEmail: row.candidateEmail || row.email,
      email: row.candidateEmail || row.email,
      interviewSessionId: type !== 'assessment' ? row._id : undefined,
      interviewId: row.interviewId,
      assessmentId: row.assessmentId,
    };

    if (type !== 'assessment') {
      openInterviewSessionForCandidate(candidate);
    } else if (row.type === 'assessment') {
      handleInlineViewAssessmentScore(candidate);
    } else if (row.type === 'resume_score') {
      openCandidateResumeBreakdown(candidate);
    }
  };

  const filtered = data.filter((item) => {
    const searchLower = searchTerm.toLowerCase();
    return (
      (item.candidateName || '').toLowerCase().includes(searchLower) ||
      (item.candidateEmail || '').toLowerCase().includes(searchLower) ||
      (item.orgName || '').toLowerCase().includes(searchLower) ||
      (item.assessmentTitle || '').toLowerCase().includes(searchLower) ||
      (item.jobTitle || '').toLowerCase().includes(searchLower) ||
      (item.status || '').toLowerCase().includes(searchLower)
    );
  });

  return (
    <div className="space-y-4">
      <div className="relative max-w-xs">
        <span className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none text-slate-400">
          <Search className="h-4 w-4" />
        </span>
        <input
          type="text"
          placeholder="Search name, client, email..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          className="pl-9 pr-4 py-1.5 w-full bg-slate-50 border border-slate-200 rounded-xl text-xs text-slate-700 focus:bg-white focus:outline-none focus:ring-1 focus:ring-indigo-500 focus:border-indigo-500 transition-colors"
        />
      </div>

      <div className="overflow-x-auto border border-slate-200/60 rounded-xl">
        <table className="w-full text-left text-xs">
          <thead>
            <tr className="border-b border-slate-200/80 bg-slate-50 text-slate-500 font-semibold uppercase tracking-wider">
              <th className="px-4 py-2.5">Candidate</th>
              <th className="px-4 py-2.5">Client</th>
              <th className="px-4 py-2.5">Date & Time</th>
              <th className="px-4 py-2.5">Status</th>
              {type !== 'assessment' ? (
                <>
                  <th className="px-4 py-2.5">Duration</th>
                  <th className="px-4 py-2.5">Score</th>
                  <th className="px-4 py-2.5 text-right">Recording</th>
                </>
              ) : (
                <>
                  <th className="px-4 py-2.5">Type</th>
                  <th className="px-4 py-2.5">Context</th>
                  <th className="px-4 py-2.5">Score</th>
                </>
              )}
              <th className="px-4 py-2.5 text-center">Action</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-100 text-slate-700 bg-white">
            {filtered.map((row) => (
              <tr key={row._id} className="hover:bg-slate-50/50 transition-colors">
                <td className="px-4 py-3">
                  <div className="font-semibold text-slate-800">{row.candidateName}</div>
                  <div className="text-[10px] text-slate-400">{row.candidateEmail}</div>
                </td>
                
                <td className="px-4 py-3 font-medium text-slate-600">{row.orgName}</td>
                
                <td className="px-4 py-3 text-slate-500">
                  {row.date ? new Date(row.date).toLocaleString('en-IN', {
                    day: '2-digit',
                    month: 'short',
                    year: 'numeric',
                    hour: '2-digit',
                    minute: '2-digit',
                  }) : '—'}
                </td>
                
                <td className="px-4 py-3">
                  <span className={cn(
                    "inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-medium uppercase tracking-wide",
                    row.status === 'completed' && "bg-emerald-50 text-emerald-700 border border-emerald-100",
                    row.status === 'in_progress' && "bg-amber-50 text-amber-700 border border-amber-100",
                    row.status === 'started' && "bg-amber-50 text-amber-700 border border-amber-100",
                    row.status === 'abandoned' && "bg-rose-50 text-rose-700 border border-rose-100",
                    row.status === 'pending' && "bg-slate-50 text-slate-600 border border-slate-150",
                    row.status === 'invited' && "bg-slate-50 text-slate-600 border border-slate-150",
                    row.status === 'shortlisted' && "bg-indigo-50 text-indigo-700 border border-indigo-100",
                    row.status === 'rejected' && "bg-rose-50 text-rose-700 border border-rose-100"
                  )}>
                    {row.status || 'unknown'}
                  </span>
                </td>

                {type !== 'assessment' ? (
                  <>
                    <td className="px-4 py-3 tabular-nums text-slate-600">
                      {row.durationMinutes != null ? `${row.durationMinutes} min` : '—'}
                    </td>
                    <td className="px-4 py-3 tabular-nums font-bold text-slate-800">
                      {row.score != null ? `${row.score}/100` : '—'}
                    </td>
                    <td className="px-4 py-3 text-right">
                      {row.hasRecording ? (
                        <RecordingPlaybackButton
                          sessionId={row._id}
                          recordingType={row.recordingType}
                        />
                      ) : (
                        <span className="text-[10px] text-slate-400">No Recording</span>
                      )}
                    </td>
                  </>
                ) : (
                  <>
                    <td className="px-4 py-3">
                      <span className={cn(
                        "inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-semibold",
                        row.type === 'assessment' ? "bg-cyan-50 text-cyan-700" : "bg-purple-50 text-purple-700"
                      )}>
                        {row.type === 'assessment' ? 'Test Assessment' : 'Resume Scoring'}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-slate-500 font-medium max-w-[200px] truncate" title={row.type === 'assessment' ? row.assessmentTitle : row.jobTitle}>
                      {row.type === 'assessment' ? row.assessmentTitle : row.jobTitle}
                    </td>
                    <td className="px-4 py-3 tabular-nums font-bold text-slate-800">
                      {row.score != null ? `${row.score}/${row.totalPoints}` : '—'}
                    </td>
                  </>
                )}

                {/* Action button column */}
                <td className="px-4 py-3 text-center">
                  <button
                    type="button"
                    disabled={!row.candidateId || !row.screeningId}
                    onClick={() => handleViewActivityDetails(row)}
                    className="inline-flex items-center gap-1 text-xs font-bold text-indigo-600 hover:text-indigo-800 transition disabled:opacity-30 disabled:cursor-not-allowed"
                    title={(!row.candidateId || !row.screeningId) ? 'Detailed logs not available for this record' : undefined}
                  >
                    {type !== 'assessment' ? 'Watch Replay' : 'View Score'}
                    <ExternalLink className="w-3 h-3" />
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <ScoreModals />
    </div>
  );
}
